<template>
  <div>
    <v-container fluid>
      <!-- ── Header (V2 family house style: primary toolbar + today on the right) ── -->
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white" data-testid="tlog-toolbar">
          <v-toolbar-title class="title">Tax Log</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- ── Intro + read-only badge ─────────────────────────────────────── -->
      <v-card class="card2" data-testid="tlog-intro-card">
        <v-card-text class="pa-4">
          <div class="d-flex align-center flex-wrap ga-2">
            <v-icon color="primary" class="mr-1" size="26">mdi-file-document-multiple-outline</v-icon>
            <h3 class="text-h6 font-weight-bold text-primary mb-0">บันทึกการสนทนา Tax Planning V2</h3>
            <v-chip
              size="small"
              color="primary"
              variant="tonal"
              label
              prepend-icon="mdi-lock-outline"
              data-testid="tlog-readonly-chip"
            >
              อ่านอย่างเดียว
            </v-chip>
          </div>
          <p class="text-caption text-medium-emphasis mt-2 mb-0" data-testid="tlog-subtitle">
            บันทึกการสนทนาของ Tax Planning V2 ทั้งจากแชทจริงและจาก Tax Playground —
            เปิดดูบทสนทนา พรอมป์ที่ส่งให้ LLM ฟอร์มคำถาม และ Tax JSON ย้อนหลังได้
            <strong>หน้านี้ดูได้อย่างเดียว ไม่สามารถแก้ไข ตอบแชท หรือรันซ้ำได้</strong>
            <strong>หนึ่งแถว = หนึ่งฟอร์มในหนึ่งเซสชัน</strong> — เซสชันเดียวที่แตะหลายฟอร์ม
            (ผู้ใช้เริ่มทำแบบสอบถามใหม่ได้หลายรอบ) จะแตกออกเป็นหลายแถว แถวละฟอร์ม
          </p>
        </v-card-text>
      </v-card>

      <!-- ── Filters ─────────────────────────────────────────────────────── -->
      <v-card class="card2" data-testid="tlog-filter-card">
        <v-card-title class="d-flex align-center justify-space-between flex-wrap ga-2">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2" size="22">mdi-filter-variant</v-icon>
            <span class="text-subtitle-1 font-weight-medium">ตัวกรอง</span>
            <v-chip
              v-if="activeFilterCount > 0"
              size="x-small"
              color="primary"
              variant="flat"
              class="ml-2"
              data-testid="tlog-active-filter-count"
            >
              {{ activeFilterCount }}
            </v-chip>
          </div>
          <v-btn
            color="warning"
            variant="text"
            size="small"
            prepend-icon="mdi-filter-remove-outline"
            data-testid="tlog-clear-filters"
            @click="clearAllFilters"
          >
            ล้างตัวกรอง
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4 pt-0">
          <!-- Date range -->
          <v-card variant="outlined" class="filter-card mb-4">
            <v-card-text class="pa-3">
              <h4 class="section-title mb-2">
                <v-icon class="mr-2" color="primary" size="20">mdi-calendar-range</v-icon>
                ช่วงวันที่
              </h4>
              <v-row class="mt-1" dense>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="fromMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    location="bottom"
                    min-width="auto"
                  >
                    <template #activator="{ props }">
                      <v-text-field
                        v-model="filters.from"
                        label="ตั้งแต่วันที่"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        clearable
                        variant="outlined"
                        density="compact"
                        color="primary"
                        hide-details
                        class="date-field"
                        data-testid="tlog-filter-from"
                        v-bind="props"
                      />
                    </template>
                    <v-date-picker
                      :max="filters.to || undefined"
                      color="primary"
                      @update:model-value="(val) => { filters.from = toIsoDate(val); fromMenu = false; }"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="toMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    location="bottom"
                    min-width="auto"
                  >
                    <template #activator="{ props }">
                      <v-text-field
                        v-model="filters.to"
                        label="ถึงวันที่"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        clearable
                        variant="outlined"
                        density="compact"
                        color="primary"
                        hide-details
                        class="date-field"
                        data-testid="tlog-filter-to"
                        v-bind="props"
                      />
                    </template>
                    <v-date-picker
                      :min="filters.from || undefined"
                      color="primary"
                      @update:model-value="(val) => { filters.to = toIsoDate(val); toMenu = false; }"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Identity / state -->
          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="filters.userId"
                label="CIF"
                placeholder="CIF เต็มค่า หรือค่าเข้ารหัสที่แสดงในตาราง (ค้นบางส่วนไม่ได้)"
                prepend-inner-icon="mdi-account"
                variant="outlined"
                density="compact"
                color="primary"
                clearable
                hide-details
                data-testid="tlog-filter-userid"
                @keyup.enter="search"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="filters.sessionId"
                label="Session ID"
                prepend-inner-icon="mdi-identifier"
                variant="outlined"
                density="compact"
                color="primary"
                clearable
                hide-details
                data-testid="tlog-filter-sessionid"
                @keyup.enter="search"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <!-- ค้นด้วยเลขฟอร์มตรง ๆ (exact match) — คู่กับคอลัมน์ Form ID ในตาราง
                   เพื่อให้รู้ว่า log ก้อนไหนเป็นของฟอร์มใบไหนโดยไม่ต้องเปิดเข้าไปดู -->
              <v-text-field
                v-model="filters.formId"
                label="Form ID"
                placeholder="เช่น 631"
                prepend-inner-icon="mdi-file-document-outline"
                type="number"
                variant="outlined"
                density="compact"
                color="primary"
                clearable
                hide-details
                data-testid="tlog-filter-formid"
                @keyup.enter="search"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                item-title="title"
                item-value="value"
                label="สถานะฟอร์ม"
                prepend-inner-icon="mdi-flag-outline"
                variant="outlined"
                density="compact"
                color="primary"
                hide-details
                data-testid="tlog-filter-status"
              />
            </v-col>
            <v-col v-if="yearOptions.length" cols="12" sm="6" md="4">
              <v-select
                v-model="filters.yearId"
                :items="yearOptions"
                item-title="title"
                item-value="value"
                label="ปีภาษี"
                prepend-inner-icon="mdi-calendar-check"
                variant="outlined"
                density="compact"
                color="primary"
                hide-details
                :loading="yearsLoading"
                data-testid="tlog-filter-year"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-combobox
                v-model="filters.channel"
                :items="channelOptions"
                label="Channel"
                prepend-inner-icon="mdi-router-wireless"
                variant="outlined"
                density="compact"
                color="primary"
                clearable
                hide-details
                data-testid="tlog-filter-channel"
                @keyup.enter="search"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-select
                v-model="filters.source"
                :items="sourceOptions"
                item-title="title"
                item-value="value"
                label="แหล่งที่มา"
                prepend-inner-icon="mdi-source-branch"
                variant="outlined"
                density="compact"
                color="primary"
                hide-details
                data-testid="tlog-filter-source"
              />
            </v-col>
          </v-row>

          <div class="d-flex justify-end mt-4">
            <v-btn
              color="primary"
              variant="elevated"
              prepend-icon="mdi-magnify"
              class="search-btn"
              :loading="loading"
              data-testid="tlog-search-btn"
              @click="search"
            >
              ค้นหา
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- ── Result table ────────────────────────────────────────────────── -->
      <v-card class="card2" data-testid="tlog-result-card">
        <v-card-title class="d-flex align-center flex-wrap ga-2">
          <v-icon color="primary" class="mr-2" size="22">mdi-format-list-bulleted</v-icon>
          <span class="text-subtitle-1 font-weight-medium">รายการทั้งหมด</span>
          <v-chip size="small" variant="tonal" color="primary" label data-testid="tlog-total-count">
            {{ formatNumber(totalCount) }} รายการ
          </v-chip>
          <v-spacer />
          <!-- เปิด viewer จากไฟล์ JSON ที่ export จากหน้า detail (ใช้ได้เสมอ ไม่ผูกกับ filter) -->
          <v-btn
            variant="outlined"
            size="small"
            prepend-icon="mdi-folder-open-outline"
            data-testid="tlog-open-offline"
            @click="openOffline"
          >
            เปิดไฟล์ JSON
          </v-btn>
          <!-- #782 — same cap + tooltip as TaxHistoryV2. The tooltip sits on a wrapper: a disabled button fires no
               hover events, and "too many forms" is exactly when the admin needs to read why. -->
          <span class="d-inline-flex">
            <v-btn
              color="success"
              variant="flat"
              size="small"
              prepend-icon="mdi-microsoft-excel"
              :loading="exportLoading"
              :disabled="exportDisabled"
              data-testid="tlog-export-excel"
              @click="exportExcel"
            >
              ส่งออก Excel
            </v-btn>
            <v-tooltip activator="parent" location="top">
              {{ exportTooMany
                ? `เกิน ${formatNumber(maxExportForms)} ฟอร์ม กรุณาเลือกช่วงวันที่ให้แคบลง`
                : 'ส่งออกรายการตามตัวกรองที่แสดงในตาราง' }}
            </v-tooltip>
          </span>
          <span class="text-caption text-medium-emphasis">
            1 แถว = 1 ฟอร์ม ต่อ 1 เซสชัน · เรียงตามกิจกรรมล่าสุด (ใหม่ → เก่า)
          </span>
        </v-card-title>

        <v-card-text class="pa-4 pt-0">
          <!-- Error state -->
          <v-alert
            v-if="error"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mb-4"
            data-testid="tlog-error"
          >
            <div class="d-flex align-center flex-wrap ga-2">
              <span>{{ error }}</span>
              <v-spacer />
              <v-btn
                color="error"
                variant="text"
                size="small"
                prepend-icon="mdi-refresh"
                data-testid="tlog-retry"
                @click="reload"
              >
                ลองใหม่
              </v-btn>
            </div>
          </v-alert>

          <v-alert
            v-if="exportError"
            type="error"
            variant="tonal"
            density="comfortable"
            closable
            class="mb-4"
            data-testid="tlog-export-error"
            @click:close="exportError = ''"
          >
            ส่งออก Excel ไม่สำเร็จ: {{ exportError }}
          </v-alert>

          <!-- First load: full skeleton instead of an empty table frame -->
          <v-skeleton-loader
            v-if="initialLoading"
            type="table-thead, table-tbody"
            data-testid="tlog-skeleton"
          />

          <div v-else class="table-wrapper">
            <v-data-table-server
              class="my-data-table"
              density="comfortable"
              hover
              data-testid="tlog-table"
              :headers="headers"
              :items="rows"
              :items-length="totalCount"
              :items-per-page="pageSize"
              :items-per-page-options="pageSizeOptions"
              :page="page"
              :loading="loading"
              :sort-by="sortBy"
              item-value="rowKey"
              @update:options="onOptions"
              @click:row="onRowClick"
            >
              <!-- Running number based on the current server page -->
              <template #item.index="{ index }">
                {{ (page - 1) * pageSize + index + 1 }}
              </template>

              <template #item.userID="{ value }">
                <CopyId :value="value" label="CIF" truncate />
              </template>

              <template #item.sessionID="{ value }">
                <CopyId :value="value" label="Session" truncate />
              </template>

              <template #item.formID="{ value }">
                <span class="mono">#{{ value }}</span>
              </template>

              <template #item.source="{ item }">
                <v-chip
                  :color="item.source === 'playground' ? 'purple' : 'teal'"
                  :prepend-icon="item.source === 'playground' ? 'mdi-flask-outline' : 'mdi-chat-outline'"
                  size="small"
                  variant="tonal"
                  label
                >
                  {{ sourceLabel(item.source) }}
                </v-chip>
              </template>

              <template #item.channel="{ value }">
                <span v-if="value">{{ value }}</span>
                <span v-else class="text-disabled">—</span>
              </template>

              <template #item.status="{ value }">
                <v-chip v-if="value" :color="statusColor(value)" size="small" variant="tonal" label>
                  {{ statusLabel(value) }}
                </v-chip>
                <span v-else class="text-disabled">—</span>
              </template>

              <template #item.yearCode="{ value }">
                <span v-if="value">{{ value }}</span>
                <span v-else class="text-disabled">—</span>
              </template>

              <template #item.taxText="{ value }">
                <span>{{ value || 'Tax Plan' }}</span>
              </template>

              <template #item.answeredCount="{ value }">
                {{ formatNumber(value) }}
              </template>

              <template #item.turnCount="{ value }">
                {{ formatNumber(value) }}
              </template>

              <template #item.totalTokens="{ value }">
                {{ formatNumber(value) }}
              </template>

              <template #item.firstActivity="{ value }">
                <span class="text-caption">{{ formatDateTime(value) }}</span>
              </template>

              <template #item.lastActivity="{ value }">
                <span class="text-caption">{{ formatDateTime(value) }}</span>
              </template>

              <template #item.actions="{ item }">
                <v-btn
                  color="primary"
                  variant="text"
                  size="small"
                  prepend-icon="mdi-eye-outline"
                  :data-testid="`tlog-view-${item.rowKey}`"
                  @click.stop="openSession(item)"
                >
                  ดู
                </v-btn>
              </template>

              <!-- Empty state -->
              <template #no-data>
                <div v-if="!loading" class="empty-state" data-testid="tlog-empty">
                  <v-icon size="40" color="grey-lighten-1" class="mb-2">mdi-text-box-search-outline</v-icon>
                  <div v-if="activeFilterCount > 0">
                    ไม่พบรายการที่ตรงกับตัวกรอง — ลองปรับช่วงวันที่หรือกด "ล้างตัวกรอง"
                  </div>
                  <div v-else>ยังไม่มีฟอร์ม Tax Planning V2 ในระบบ</div>
                </div>
              </template>
            </v-data-table-server>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
/**
 * Tax Log V2 — READ-ONLY list of Tax Planning V2 chat sessions.
 *
 * 🔴 GET-ONLY. This view calls exactly two read endpoints
 * (`taxLogV2Api.listSessions` and `taxConfigV2Api.getYears`) and owns no write
 * affordance whatsoever — no create/edit/delete/reset/re-run. The only mutation
 * it performs is `$router.replace` on the OWN query string so a refresh keeps
 * the filter. Do not add a mutating call here; the service layer deliberately
 * does not even define `post`/`put`/`del` (see `services/taxLogV2Api.js`).
 *
 * 🔴 ONE ROW = ONE (FORM × SESSION) PAIR, not one session. A real LHB session
 * restarts the questionnaire and accumulates many `tax.TaxFormV2` rows (verified:
 * one session held 32) — that session now renders as 32 rows, each carrying its
 * OWN `formID` / `status` / `yearCode` / `taxText` / `answeredCount`. The wire
 * used to collapse the pairs and send `formCount` + `latest*` fields; both are
 * gone. Consequences that are easy to get wrong:
 *   • `sessionID` is NOT unique across rows ⇒ the table is keyed by `rowKey`
 *     (`"<sessionID>::<formID>"`), never by `sessionID` alone.
 *   • `turnCount` / `totalTokens` / `firstActivity` / `lastActivity` are still
 *     SESSION-level (a turn cannot be attributed to a form — `dbo.Log_Message`
 *     has no form column) so they REPEAT across the rows of one session. The two
 *     count columns say "ทั้งเซสชัน" in their header so the repetition reads as
 *     intent rather than as a bug.
 *   • Opening a row deep-links the form of THAT row (`?form=<formID>`), so the
 *     detail view does not silently fall back to the newest one.
 */
import CopyId from '../components/CopyId.vue';
import { taxLogV2Api } from '../services/taxLogV2Api.js';
import { taxConfigV2Api } from '../services/taxConfigV2Api.js';
import { fmtThaiDateTime } from '../utils/thaiTime.js';
import { collectExportRows, downloadTaxWorkbook, exportTimestamp } from '../utils/taxV2ExcelExport.js';

const DEFAULT_PAGE_SIZE = 25;

/** #782 — same cap as TaxHistoryV2 (MAX_EXPORT_FORMS); past it the client-side Excel build pages through too much. */
const MAX_EXPORT_FORMS = 2000;

export default {
  name: 'TaxLogV2',
  components: { CopyId },
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),

      // ── filter model (mirrors TaxLogV2Filter on the wire) ──────────────
      filters: {
        userId: '',
        sessionId: '',
        formId: '', // exact match; '' = ไม่กรอง (ดู `qs()` ใน taxLogV2Api.js)
        status: null,
        yearId: null,
        channel: '',
        source: '', // '' = ทั้งหมด | 'production' | 'playground'
        from: '', // YYYY-MM-DD
        to: '', // YYYY-MM-DD
      },
      fromMenu: false,
      toMenu: false,

      // ── result ────────────────────────────────────────────────────────
      items: [],
      totalCount: 0,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      pageSizeOptions: [25, 50, 100],
      loading: false,
      initialLoading: true,
      error: '',
      exportLoading: false,
      exportError: '',
      maxExportForms: MAX_EXPORT_FORMS,
      appliedFilters: null,

      // The API exposes no sort parameter — the server always orders by
      // LastActivity DESC. `sortBy` is bound only so the table's header state
      // matches reality; every column is `sortable: false` so a click cannot
      // promise a re-sort the backend will not honour.
      sortBy: [{ key: 'lastActivity', order: 'desc' }],

      // v-data-table-server emits `update:options` once on mount; created()
      // owns the first load, so that very first echo is swallowed.
      optionsReady: false,

      years: [],
      yearsLoading: false,

      statusOptions: [
        { title: 'ทั้งหมด', value: null },
        { title: 'กำลังทำ (InProgress)', value: 'InProgress' },
        { title: 'เสร็จสิ้น (Completed)', value: 'Completed' },
        { title: 'ยกเลิก (Abandoned)', value: 'Abandoned' },
      ],
      sourceOptions: [
        { title: 'ทั้งหมด', value: '' },
        { title: 'แชทจริง', value: 'production' },
        { title: 'Playground', value: 'playground' },
      ],

      // 🔴 ทุกคอลัมน์ `sortable: false` — API ไม่มีพารามิเตอร์ sort เลย (เรียง LastActivity DESC เสมอ)
      //    หัวคอลัมน์ที่กดแล้วไม่เกิดอะไรคือคำสัญญาลม อย่าเปิดจนกว่าจะมี server-side sort จริง
      headers: [
        { title: 'ลำดับ', key: 'index', sortable: false, width: 64 },
        { title: 'CIF', key: 'userID', sortable: false, width: 180 },
        { title: 'Session ID', key: 'sessionID', sortable: false, width: 200 },
        // แถวเป็นของฟอร์มใบนี้ใบเดียว — คอลัมน์ที่บรรยายฟอร์มด้านล่างจึงเป็นค่าของ Form ID นี้
        { title: 'Form ID', key: 'formID', sortable: false, width: 110 },
        { title: 'แหล่งที่มา', key: 'source', sortable: false, width: 130 },
        { title: 'Channel', key: 'channel', sortable: false, width: 120 },
        { title: 'สถานะ', key: 'status', sortable: false, width: 130 },
        { title: 'ปีภาษี', key: 'yearCode', sortable: false, width: 100 },
        { title: 'ชื่อแผน', key: 'taxText', sortable: false, width: 180 },
        { title: 'ตอบแล้ว', key: 'answeredCount', sortable: false, align: 'end', width: 96 },
        // 🔴 สองคอลัมน์นี้เป็นค่าระดับ "เซสชัน" ไม่ใช่ของฟอร์มในแถว (turn ผูกกับฟอร์มไม่ได้ —
        //    `dbo.Log_Message` ไม่มีคอลัมน์ FormID) ⇒ ทุกแถวของ session เดียวกันจะได้เลขเท่ากัน
        //    ป้าย "ทั้งเซสชัน" มีไว้กันคนอ่านเข้าใจว่าเป็นเทิร์นของฟอร์มใบนั้น
        { title: 'เทิร์น (ทั้งเซสชัน)', key: 'turnCount', sortable: false, align: 'end', width: 130 },
        { title: 'Tokens (ทั้งเซสชัน)', key: 'totalTokens', sortable: false, align: 'end', width: 140 },
        { title: 'เริ่มเมื่อ', key: 'firstActivity', sortable: false, width: 150 },
        { title: 'ล่าสุด', key: 'lastActivity', sortable: false, width: 150 },
        { title: 'ดู', key: 'actions', sortable: false, align: 'center', width: 90 },
      ],
    };
  },
  computed: {
    /**
     * 🔴 The table's identity column. A row is a (FORM × SESSION) PAIR, so
     * `sessionID` REPEATS across the rows of a session that touched several forms
     * — keying `v-data-table-server` by it (as this page used to) collides those
     * rows and Vue reuses/drops DOM between them. `rowKey` is the pair, minted
     * here rather than mutating the wire objects.
     */
    rows() {
      return (this.items || []).map((it) => ({
        ...it,
        rowKey: `${it && it.sessionID}::${(it && it.formID) || 0}`,
      }));
    },
    // Badge next to "ตัวกรอง" — how many filters are actually narrowing the result.
    activeFilterCount() {
      const f = this.filters;
      let n = 0;
      if (f.userId) n += 1;
      if (f.sessionId) n += 1;
      if (f.formId) n += 1;
      if (f.status) n += 1;
      if (f.yearId) n += 1;
      if (f.channel) n += 1;
      if (f.source) n += 1;
      if (f.from) n += 1;
      if (f.to) n += 1;
      return n;
    },
    exportTooMany() {
      return this.totalCount > MAX_EXPORT_FORMS;
    },
    exportDisabled() {
      return this.loading || this.initialLoading || this.exportLoading || !!this.error || this.totalCount < 1
        || !this.appliedFilters || this.exportTooMany;
    },
    // Tax years come from the V2 config service (the only place that owns them).
    // If the call fails the array stays empty and the whole year filter is
    // hidden (v-if) rather than rendered as a dead dropdown.
    yearOptions() {
      if (!this.years.length) return [];
      return [{ title: 'ทั้งหมด', value: null }].concat(
        this.years.map((y) => ({
          title: y.taxYearCode || `ปี ${y.beYear || y.yearID}`,
          value: y.yearID,
        }))
      );
    },
    // Channel suggestions = page-local distinct of what is loaded. The combobox
    // still accepts free text, so a channel outside the current page is typable.
    channelOptions() {
      const seen = new Set();
      this.items.forEach((it) => {
        if (it.channel) seen.add(it.channel);
      });
      return Array.from(seen).sort();
    },
  },
  created() {
    this.hydrateFromQuery();
    this.loadYears();
    this.fetch(this.page, this.pageSize);
  },
  methods: {
    // ── formatting helpers ────────────────────────────────────────────
    /** `2026-07-28` from whatever the date picker hands back (Date | string). */
    toIsoDate(value) {
      if (!value) return '';
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    },
    /**
     * 🔴 เวลาไทยเสมอ ไม่ใช่เวลาเครื่อง — `firstActivity`/`lastActivity` มาจาก `dbo.Log_Message.CreateDate`
     * ซึ่งเขียนเป็น UTC แต่ส่งมาบน wire โดยไม่มี `Z` ⇒ `new Date(v)` เดิมตีเป็นเวลาท้องถิ่นแล้วโชว์ช้าไป 7 ชม.
     * (เหตุผลเต็ม ๆ อยู่หัวไฟล์ `utils/thaiTime.js`)
     */
    formatDateTime(value) {
      return fmtThaiDateTime(value);
    },
    formatNumber(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '0';
      return n.toLocaleString('th-TH');
    },
    sourceLabel(source) {
      return source === 'playground' ? 'Playground' : 'แชทจริง';
    },
    statusLabel(status) {
      if (status === 'InProgress') return 'กำลังทำ';
      if (status === 'Completed') return 'เสร็จสิ้น';
      if (status === 'Abandoned') return 'ยกเลิก';
      return status;
    },
    statusColor(status) {
      if (status === 'InProgress') return 'warning';
      if (status === 'Completed') return 'success';
      if (status === 'Abandoned') return 'grey';
      return 'grey';
    },

    // ── query-string persistence (refresh keeps the filter) ───────────
    hydrateFromQuery() {
      const q = this.$route.query || {};
      const str = (v) => (typeof v === 'string' ? v : Array.isArray(v) ? v[0] || '' : '');
      this.filters.userId = str(q.userId);
      this.filters.sessionId = str(q.sessionId);
      const formId = parseInt(str(q.formId), 10);
      this.filters.formId = Number.isFinite(formId) && formId > 0 ? String(formId) : '';
      this.filters.status = str(q.status) || null;
      this.filters.channel = str(q.channel);
      this.filters.from = str(q.from);
      this.filters.to = str(q.to);
      const source = str(q.source);
      this.filters.source = source === 'production' || source === 'playground' ? source : '';
      const yearId = parseInt(str(q.yearId), 10);
      this.filters.yearId = Number.isFinite(yearId) && yearId > 0 ? yearId : null;
      const page = parseInt(str(q.page), 10);
      if (Number.isFinite(page) && page > 0) this.page = page;
      const pageSize = parseInt(str(q.pageSize), 10);
      if (this.pageSizeOptions.includes(pageSize)) this.pageSize = pageSize;
    },
    /** Mirror the current filter + paging into the URL (replace → no history spam). */
    syncQuery() {
      const q = {};
      const f = this.filters;
      if (f.userId) q.userId = f.userId;
      if (f.sessionId) q.sessionId = f.sessionId;
      if (f.formId) q.formId = String(f.formId);
      if (f.status) q.status = f.status;
      if (f.yearId) q.yearId = String(f.yearId);
      if (f.channel) q.channel = f.channel;
      if (f.source) q.source = f.source;
      if (f.from) q.from = f.from;
      if (f.to) q.to = f.to;
      if (this.page > 1) q.page = String(this.page);
      if (this.pageSize !== DEFAULT_PAGE_SIZE) q.pageSize = String(this.pageSize);
      // Skip identical navigations (Vue Router warns on duplicates).
      const current = this.$route.query || {};
      const same =
        Object.keys(q).length === Object.keys(current).length &&
        Object.keys(q).every((k) => String(current[k]) === q[k]);
      if (same) return;
      this.$router.replace({ query: q }).catch(() => {
        /* redundant navigation — harmless */
      });
    },

    // ── data ──────────────────────────────────────────────────────────
    async loadYears() {
      this.yearsLoading = true;
      try {
        const rows = await taxConfigV2Api.getYears(true);
        this.years = Array.isArray(rows) ? rows.slice() : [];
        // Newest first, so the current tax year is at the top of the dropdown.
        this.years.sort((a, b) => (b.beYear || 0) - (a.beYear || 0));
      } catch {
        // Years are a convenience filter only — a failure hides the dropdown
        // (yearOptions === []) instead of blocking the whole page.
        this.years = [];
      } finally {
        this.yearsLoading = false;
      }
    },
    async fetch(page, pageSize) {
      this.loading = true;
      this.error = '';
      const f = this.filters;
      // Free-text fields may hold a combobox object or stray whitespace.
      const channel = typeof f.channel === 'string' ? f.channel.trim() : String(f.channel ?? '').trim();
      const request = {
        userId: (f.userId || '').trim(),
        sessionId: (f.sessionId || '').trim(),
        // ⚠️ `qs()` ตัด 0/''/null ทิ้ง — ช่องว่างต้องกลายเป็น null ไม่ใช่ NaN
        formId: Number(f.formId) > 0 ? Number(f.formId) : null,
        status: f.status || '',
        yearId: f.yearId || 0,
        channel,
        source: f.source || '',
        from: f.from || '',
        to: f.to || '',
        page,
        pageSize,
      };
      try {
        const result = await taxLogV2Api.listSessions(request);
        this.items = (result && result.items) || [];
        this.totalCount = (result && result.totalCount) || 0;
        this.page = (result && (result.page || result.pageNumber)) || page;
        this.pageSize = (result && result.pageSize) || pageSize;
        this.appliedFilters = Object.freeze({
          userId: request.userId,
          sessionId: request.sessionId,
          formId: request.formId,
          status: request.status,
          yearId: request.yearId,
          channel: request.channel,
          source: request.source,
          from: request.from,
          to: request.to,
        });
      } catch (err) {
        this.items = [];
        this.totalCount = 0;
        this.error = (err && err.message) || 'โหลดรายการไม่สำเร็จ';
      } finally {
        this.loading = false;
        this.initialLoading = false;
        this.syncQuery();
      }
    },
    async exportExcel() {
      if (this.exportDisabled) return;
      this.exportLoading = true;
      this.exportError = '';
      try {
        const sourceRows = await collectExportRows({
          filters: this.appliedFilters,
          rowKey: (row) => `${row.source || ''}|${row.sessionID || ''}|${row.formID ?? ''}`,
          fetchPage: (request) => taxLogV2Api.listSessions(request),
        });
        const rows = sourceRows.map((row, index) => ({
          index: index + 1,
          userID: String(row.userID ?? ''),
          sessionID: String(row.sessionID ?? ''),
          formID: String(row.formID ?? ''),
          source: this.sourceLabel(row.source),
          channel: String(row.channel ?? ''),
          status: this.statusLabel(row.status) || '',
          yearCode: String(row.yearCode ?? ''),
          taxText: String(row.taxText ?? ''),
          answeredCount: Number(row.answeredCount) || 0,
          turnCount: Number(row.turnCount) || 0,
          totalTokens: Number(row.totalTokens) || 0,
          firstActivity: this.formatDateTime(row.firstActivity),
          lastActivity: this.formatDateTime(row.lastActivity),
        }));
        await downloadTaxWorkbook({
          filename: `Tax_Log_V2_${exportTimestamp()}.xlsx`,
          sheetName: 'Tax Log V2',
          columns: this.headers
            .filter((column) => column.key !== 'actions')
            .map((column) => ({ header: column.title, key: column.key, width: Math.max(10, Math.round((column.width || 120) / 8)) })),
          rows,
        });
      } catch (error) {
        this.exportError = error?.message || 'ไม่สามารถสร้างไฟล์ Excel ได้';
      } finally {
        this.exportLoading = false;
      }
    },
    onOptions(opts) {
      // Swallow the table's mount echo — created() already did the first load.
      if (!this.optionsReady) {
        this.optionsReady = true;
        return;
      }
      const page = opts && opts.page ? opts.page : 1;
      const pageSize = opts && opts.itemsPerPage ? opts.itemsPerPage : this.pageSize;
      // `:page` is a controlled prop, so a programmatic reset (search → page 1)
      // makes the table echo back the state we just fetched with. Only a genuine
      // user paging action differs from the current state — fetch on that alone,
      // otherwise every filter change from page > 1 would fire two requests.
      if (page === this.page && pageSize === this.pageSize) return;
      this.fetch(page, pageSize);
    },
    /** Filter changed → back to page 1. */
    search() {
      this.fetch(1, this.pageSize);
    },
    reload() {
      this.fetch(this.page || 1, this.pageSize);
    },
    clearAllFilters() {
      this.filters = {
        userId: '',
        sessionId: '',
        formId: '',
        status: null,
        yearId: null,
        channel: '',
        source: '',
        from: '',
        to: '',
      };
      this.fetch(1, this.pageSize);
    },

    // ── navigation (read-only detail viewer) ──────────────────────────
    openSession(item) {
      // 🔴 camelCase wire: C# `SessionID` → JSON `sessionID` (capital ID).
      if (!item || !item.sessionID) return;
      // 🔴 A row names ONE form of the session, so hand that id to the detail
      // view (`?form=`) — without it the viewer falls back to `forms[0]` (the
      // NEWEST form) and an admin clicking row "Form #612" would land on #631.
      // Query string, not a route param: the route shape (and therefore
      // `APIAuthen` / router / NavBar) stays untouched. A `?form=` the session
      // does not own is ignored there, so a stale link degrades, never errors.
      const formId = Number(item.formID) > 0 ? Number(item.formID) : null;
      this.$router.push({
        name: 'TaxLogV2Detail',
        params: { sessionId: item.sessionID },
        ...(formId ? { query: { form: String(formId) } } : {}),
      });
    },
    onRowClick(_event, row) {
      this.openSession(row && row.item);
    },
    // เปิด viewer ออฟไลน์ (route TaxLogV2Offline) สำหรับไฟล์ export ของหน้า detail
    openOffline() {
      this.$router.push({ name: 'TaxLogV2Offline' });
    },
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 16px !important;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.filter-card {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  transition: box-shadow 0.3s ease;
}
.filter-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}

.section-title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  border-bottom: 2px solid rgba(25, 118, 210, 0.2);
  padding-bottom: 8px;
}

.date-field :deep(.v-field) {
  border-radius: 8px !important;
}

.search-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  min-width: 120px !important;
}

.table-wrapper {
  max-height: 640px;
  overflow: auto;
}

/* Rows are clickable (open the read-only viewer) — hint it. */
.my-data-table :deep(tbody tr) {
  cursor: pointer;
}

/* Form ID reads as an identifier, not prose — same treatment as TaxHistoryV2. */
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.empty-state {
  padding: 36px 16px;
  text-align: center;
  color: #888;
}

@media (prefers-reduced-motion: reduce) {
  .filter-card {
    transition: none;
  }
}
</style>
